import {
  Autocomplete,
  Button,
  CircularProgress,
  Container,
  Grid,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
  Checkbox,
} from "@mui/material"
import { useState } from "react"
import { Helmet } from "react-helmet-async"
import { toast } from "react-toastify"
import { useAppSelector } from "../../app/hooks"
import { RootState } from "../../app/store"
import { useCalculateNetPriceMutation, useGetManufacturerQuery } from "../../services/API/pricingRuleAPI"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { getApiErrorMessageV2 } from "../../utils/getApiErrorMessage"
import SearchableCustomerDDSS from "./components/SerchableCustomerDDSS"

const itemType = [
  { title: "Part", enum: "Part" },
  { title: "Whole Good", enum: "WholeGood" },
  { title: "Service", enum: "Service" },
]

const orderType = [
  { title: "SO/Quote", enum: "SOQuote" },
  { title: "RO/Estimate", enum: "ROEstimate" },
  { title: "Rental", enum: "Rental" },
  { title: "Warranty", enum: "Warranty" },
]

const customers = [
  { title: "C A S H   S A L E", id: "100001" },
  { title: "Employee, Dealer", id: "100910" },
  { title: "Customer, Online", id: "104405" },
  { title: "Krisnet Golf & Grass", id: "104490" },
  { title: "DUCKS UNLIMITED", id: "104330" },
  { title: "Duck, Donald", id: "106459" },
  { title: "North Pole Inc", id: "106461" },
  { title: "Mouse, Micky", id: "106468" },
]

const CalculatePrice = () => {
  const formatCurrency = (value: string | number | null) => {
    if (value === null || value === undefined || value === "") return ""

    const numericValue = typeof value === "number" ? value : parseFloat(value)

    if (Number.isNaN(numericValue)) return ""

    return numericValue.toFixed(2)
  }

  const [calculatePriceForm, setCalculatePriceForm] = useState({
    inventoryWGId: "",
    manufacturerCode: "",
    partNumber: "",
    quantity: 1,
    itemType: "",
    orderType: "",
    customerId: "",
    orderDate: null,
    onlineOrder: false,
    discountCode: "",
  })

  const [calculatedPrice, setCalculatedPrice] = useState({
    dealerListPrice: "",
    netPrice: "",
    rulesApplied: "",
    msrp: "",
    cost: "",
  })

  const [customer, setCustomer] = useState<ICustomers | null>(null)

  /*
   * Global States.
   * */

  const dealerGuid = useAppSelector(
    (state: RootState) => state.auth.dealerGuid,
  )

  const companyDealerId = useAppSelector(
    (state: RootState) => state.auth.companyDealerId,
  )

  /*
   * API.
   * */

  const [calculateNetPrice, { isLoading: netPriceIsFetching }] =
    useCalculateNetPriceMutation()

  const { data: manufacturers = [] } = useGetManufacturerQuery({
    companyDealerId,
  })

  /*
   * Handlers.
   * */

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target

    setCalculatePriceForm((prevState) => ({
      ...prevState,
      [name]: value,
    }))
  }

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target

    setCalculatePriceForm((prevState) => ({
      ...prevState,
      [name]: checked,
    }))
  }

  const handleKeyDown = (event: any) => {
    if (event.key === "e") {
      event.preventDefault()
    }
  }

  const handleDateChange = (newValue: any) => {
    setCalculatePriceForm((prevState) => ({
      ...prevState,
      orderDate: newValue,
    }))
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    try {
      setCalculatedPrice({
        dealerListPrice: "",
        netPrice: "",
        rulesApplied: "",
        msrp: "",
        cost: "",
      })

      // Build the POST body explicitly to avoid relying on async setState
      const body = {
        dealerGuid,
        inventoryWGId: calculatePriceForm.inventoryWGId || null,
        manufacturerCode: calculatePriceForm.manufacturerCode || null,
        partNumber: calculatePriceForm.partNumber || null,
        quantity: calculatePriceForm.quantity,
        itemType: calculatePriceForm.itemType,
        orderType: calculatePriceForm.orderType,
        orderDate: calculatePriceForm.orderDate
          ? new Date(calculatePriceForm.orderDate).toISOString()
          : null,
        onlineOrder: calculatePriceForm.onlineOrder,
        customerId: customer?.customerId ?? (calculatePriceForm.customerId || null),
        discountCode: calculatePriceForm.discountCode || null,
      }

      const res = await calculateNetPrice(body as any)

      if ("error" in res) throw res.error

      // Display the calculated price
      if ("data" in res) {
        setCalculatedPrice(res?.data as any)
        toast.success("Price Calculated Successfully")
      }
    } catch (err) {
      if ((err as any)?.error?.status == 401) {
        toast.error("User Unauthorized, Please Refresh the Page.")
      } else {
        toast.error(getApiErrorMessageV2(err))
      }
      // toast.error("Error in Getting Price")
      // console.error("Error:", error)
    }
  }

  return (
    <>
      <Helmet>
        <title> Calculate Price </title>
      </Helmet>

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h6" gutterBottom>
            Calculate Price
          </Typography>
        </Stack>

        <form onSubmit={handleSubmit}>
          <Grid container rowSpacing={3} columnSpacing={2}>
            {/* Left Grid */}
            <Grid item xs={4}>
              <TextField
                sx={{ mb: 2 }}
                fullWidth
                label="Wholegood Id"
                name="inventoryWGId"
                value={calculatePriceForm.inventoryWGId}
                onChange={handleChange}
                size="small"
                type="text"
                variant="outlined"
              />

              <Autocomplete
                fullWidth
                sx={{ mb: 2 }}
                size="small"
                options={manufacturers}
                getOptionLabel={(option: any) => option.name || ""}
                value={manufacturers.find((m: any) => m.manufacturerId === calculatePriceForm.manufacturerCode) || null}
                onChange={(event, newValue) => {
                  setCalculatePriceForm((prevState) => ({
                    ...prevState,
                    manufacturerCode: newValue?.manufacturerId || "",
                  }))
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Manufacturer"
                    variant="outlined"
                    size="small"
                  />
                )}
              />

              <TextField
                sx={{ mb: 2 }}
                fullWidth
                label="Part Number"
                name="partNumber"
                value={calculatePriceForm.partNumber}
                onChange={handleChange}
                size="small"
                type="text"
                variant="outlined"
              />

              <TextField
                sx={{ mb: 1 }}
                fullWidth
                label="Quantity"
                name="quantity"
                type="number"
                variant="outlined"
                placeholder="Quantity"
                size="small"
                value={calculatePriceForm.quantity}
                onChange={handleChange}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  min: -100000,
                }}
                onKeyDown={handleKeyDown}
              />

              <TextField
                sx={{ mb: 1 }}
                fullWidth
                required
                label="Item Type"
                placeholder="Item Type"
                name="itemType"
                size="small"
                select
                margin="dense"
                value={calculatePriceForm.itemType}
                onChange={handleChange}
              >
                {itemType?.map((item: any) => (
                  <MenuItem key={item.enum} value={item.enum}>
                    {item.title}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                sx={{ mb: 1 }}
                fullWidth
                required
                label="Order Type"
                placeholder="Order Type"
                name="orderType"
                size="small"
                select
                margin="dense"
                value={calculatePriceForm.orderType}
                onChange={handleChange}
              >
                {orderType?.map((item: any) => (
                  <MenuItem key={item.enum} value={item.enum}>
                    {item.title}
                  </MenuItem>
                ))}
              </TextField>

              <Stack direction="row" alignItems="center">
                <Checkbox
                  name="onlineOrder"
                  inputProps={{ "aria-label": "controlled" }}
                  checked={calculatePriceForm.onlineOrder}
                  onChange={handleCheckboxChange}
                  sx={{ mb: 1 }}
                />
                <Typography variant="body2" sx={{ mr: 2, mb: 1 }}>
                  Online Order
                </Typography>
              </Stack>

              <SearchableCustomerDDSS
                        customer={customer}
                        setCustomer={setCustomer}
                      />

              <DatePicker
                sx={{ mb: 2, width: "100%" }}
                value={calculatePriceForm.orderDate}
                onChange={handleDateChange}
              />

              <TextField
                sx={{ mb: 4 }}
                fullWidth
                label="Discount Code"
                name="discountCode"
                value={calculatePriceForm.discountCode}
                onChange={handleChange}
                size="small"
                type="text"
                variant="outlined"
              />

              {netPriceIsFetching ? (
                <Button
                  variant="contained"
                  type="submit"
                  disabled={netPriceIsFetching}
                  endIcon={<CircularProgress size="1.5rem" />}
                >
                  Calculate Price
                </Button>
              ) : (
                <Button variant="contained" type="submit">
                  Calculate Price
                </Button>
              )}
            </Grid>

            {/* Right Grid */}
            <Grid
              item
              xs={8}
              display="flex"
              flexDirection="column"
              alignItems="center"
            >
              <TextField
                disabled
                sx={{ mb: 2, width: "45%" }}
                label="Dealer List Price"
                name="dealerListPrice"
                value={formatCurrency(calculatedPrice.dealerListPrice)}
                size="small"
                type="text"
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
              />
              <TextField
                disabled
                sx={{ mb: 2, width: "45%" }}
                label="Net Price"
                name="netPrice"
                value={formatCurrency(calculatedPrice.netPrice)}
                size="small"
                type="text"
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
              />
              <TextField
                disabled
                sx={{ mb: 2, width: "45%" }}
                label="Rules Applied"
                name="rulesApplied"
                value={calculatedPrice.rulesApplied}
                size="small"
                type="text"
                variant="outlined"
                multiline
                rows={4}
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  sx: {
                    overflowY: "auto",
                  },
                }}
              />
              <TextField
                disabled
                sx={{ mb: 2, width: "45%" }}
                label="MSRP"
                name="msrp"
                value={formatCurrency(calculatedPrice.msrp)}
                size="small"
                type="text"
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
              />
              <TextField
                disabled
                sx={{ mb: 2, width: "45%" }}
                label="Cost"
                name="cost"
                value={formatCurrency(calculatedPrice.cost)}
                size="small"
                type="text"
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </form>
      </Container>
    </>
  )
}

export default CalculatePrice
